import type { JpegQuality } from './lib/worker-protocol';

interface ToolDefinition {
  name: string;
  title?: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: {
    readOnlyHint?: boolean;
    untrustedContentHint?: boolean;
  };
  execute(input: unknown): unknown | Promise<unknown>;
}

interface ModelContext {
  registerTool(
    tool: ToolDefinition,
    options?: { signal?: AbortSignal },
  ): void | Promise<void>;
}

declare global {
  interface Document {
    readonly modelContext?: ModelContext;
  }
}

export interface ConverterStatus {
  quality: JpegQuality;
  selected: number;
  queued: number;
  converting: number;
  ready: number;
  errors: number;
  running: boolean;
}

export function registerConverterTools(options: {
  getStatus: () => ConverterStatus;
  convertSelected: () => Promise<ConverterStatus>;
}): () => void {
  const context = document.modelContext;
  if (!context?.registerTool) return () => undefined;

  const lifecycle = new AbortController();
  const register = (tool: ToolDefinition) => {
    try {
      void Promise.resolve(
        context.registerTool(tool, { signal: lifecycle.signal }),
      ).catch(() => undefined);
    } catch {
      // Unsupported or incomplete experimental implementations fail quietly.
    }
  };

  register({
    name: 'get_heic_conversion_status',
    title: 'Get HEIC conversion status',
    description:
      'Read the selected-file counts, JPEG quality, and current conversion state.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: () => options.getStatus(),
  });

  register({
    name: 'convert_selected_heic_files',
    title: 'Convert selected HEIC files',
    description:
      'Start the same sequential HEIC-to-JPG conversion shown in the page for files the person already selected.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false },
    execute: () => options.convertSelected(),
  });

  return () => lifecycle.abort();
}
